'use client'
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { getServiceableArea } from '../repositories/storeRepository'
import getStoreIdBasedOnHasPicking from '../utils/getStoreIdBasedOnHasPicking'
import { CHECKOUT_KEY, STORE_ID } from '../utils/constDictionary'
import { setCookie, clearCookie } from '../lib'
import isEmpty from 'lodash/isEmpty'

const DELIVERY = 'DELIVERY'

const initialState = {
  address: {},
  hub: {},
  isAddressPresent: false,
  isAddressSelectorPopup: false,
  isChangeAddressPopup: false,
  showAddNewAddressPopup: false,
  addAddress: false,
  useMyLocationEnabled: false,
  useMyLocationClicked: false,
  isAddressSelectorPopupInCenter: false,
  buildingDeliveryEnabled: false,
  error: '',
}

const handleCookieStorage = async (address) => {
  try {
    if (address?.address) {
      const addressForCookie = {
        [CHECKOUT_KEY]: JSON.stringify(address),
        [STORE_ID]: address?.address?.storeId || address?.storeId,
      }
      await setCookie(addressForCookie)
    } else {
      await clearCookie(CHECKOUT_KEY)
      await clearCookie(STORE_ID)
    }
  } catch (error) {
    console.error('Error updating cookies:', error)
  }
}

export const validateAndUpdateAddress = createAsyncThunk(
  'address/validateAndUpdateAddress',
  async ({ address, addressType = 'shippingAddress' }, { dispatch }) => {
    try {
      const response = await getServiceableArea({
        params: {
          latitude: address?.latitude,
          longitude: address?.longitude,
          pincode: address?.pincode,
          state: address?.state,
          countryCode: address?.countryCode,
        },
      })
      if (response) {
        const storeIdBasedOnHasPicking = getStoreIdBasedOnHasPicking(
          response.store
        )
        const storeId = storeIdBasedOnHasPicking
        const storeName = response.store.name

        const hub = {
          hasPicking: response.store?.hasPicking,
          originalStoreId: response.store?.id,
          pickingStoreId: response.store?.pickingStoreId,
        }

        const payload = {
          address: {
            address: {
              ...address,
              type: DELIVERY,
              storeId,
              storeName: storeName || address?.storeName,
            },
            hub,
            isAddressPresent: true,
          },
        }

        if (addressType !== 'billingAddress') {
          dispatch(updateAddress(payload))
        }
      }
      return response
    } catch (error) {
      console.error(error)
      throw error
    }
  }
)

export const serviceable = createAsyncThunk(
  'address/serviceable',
  async ({ checkout, organizationData }, { dispatch }) => {
    try {
      if (!checkout) return {}

      const storId = checkout?.metaData?.storeId && {
        storeId: checkout?.metaData?.storeId,
      }
      const addresId = checkout?.id && {
        addressId: checkout?.id,
      }

      const response = await getServiceableArea({
        params: {
          latitude: checkout.latitude,
          longitude: checkout.longitude,
          pincode: checkout.pincode,
          state: checkout.state,
          countryCode: checkout.countryCode,
          ...(organizationData?.theme?.name === 'Getz' && (storId || addresId)),
        },
      })

      if (response) {
        const storeIdBasedOnHasPicking = getStoreIdBasedOnHasPicking(
          response.store
        )
        const storeId = response.store && storeIdBasedOnHasPicking

        const hub = {
          hasPicking: response.store?.hasPicking,
          originalStoreId: response.store?.id,
          pickingStoreId: response.store?.pickingStoreId,
        }
        const storeName = response.store && response.store.name
        const { deliveryFee } = response

        const payload = {
          address: {
            address: {
              address: checkout,
              storeId,
              deliveryFee,
              storeName,
              type: DELIVERY,
            },
            hub,
            isAddressPresent: true,
          },
        }
        dispatch(updateAddress(payload))
      }
      return response
    } catch (error) {
      console.error(error)
      throw error
    }
  }
)

export const initializeAddressState = createAsyncThunk(
  'address/initializeAddressState',
  async ({ organizationData, checkoutAddress, isLoggedin }, { dispatch }) => {
    try {
      const buildingDeliveryEnabled = !isEmpty(
        organizationData?.config?.address?.building
      )

      const checkoutBuildingDataPresent = !isEmpty(
        checkoutAddress?.address?.metaData
      )

      const checkForMetaName = checkoutAddress?.address?.metaData?.name
      let address = checkoutAddress || {}
      if (
        // eslint-disable-next-line no-bitwise
        Boolean(buildingDeliveryEnabled) ^
          Boolean(checkoutBuildingDataPresent) &&
        !checkForMetaName
      ) {
        address = {}
      }

      const isAddressPresent =
        Boolean(address?.address) ||
        (typeof address !== 'object' && Boolean(address))
      // First update the cookies
      await handleCookieStorage(address)

      // Then update the Redux store
      dispatch(
        updateAddress({
          address,
          isAddressPresent: isAddressPresent,
        })
      )

      dispatch(setBuildingDeliveryEnabled(buildingDeliveryEnabled))

      return { address, buildingDeliveryEnabled }
    } catch (error) {
      console.error('Error initializing address state:', error)
      throw error
    }
  }
)

export const handleLoginStateChange = createAsyncThunk(
  'address/handleLoginStateChange',
  async (
    { isLoggedin, userData, organizationData },
    { dispatch, getState }
  ) => {
    try {
      const state = getState()

      // If user logs in and there's no address, use default address
      if (isLoggedin && !state.address.address?.address) {
        const defaultCheckoutAddress = userData.defaultAddress
        if (defaultCheckoutAddress) {
          await dispatch(
            serviceable({
              checkout: defaultCheckoutAddress,
              organizationData,
            })
          )
        }
      }

      // If user logs out, reset address state
      if (!isLoggedin) {
        dispatch(resetAddress())
      }

      return { isLoggedin }
    } catch (error) {
      console.error('Error handling login state change:', error)
      throw error
    }
  }
)

export const updateAddressWithCookie = createAsyncThunk(
  'address/updateAddressWithCookie',
  async ({ address, hub, isAddressPresent }, { dispatch }) => {
    // First update the Redux store
    dispatch(updateAddress({ address, hub, isAddressPresent }))
    // Then update the cookies
    await handleCookieStorage(address)
    return { address, hub, isAddressPresent }
  }
)

const addressSlice = createSlice({
  name: 'address',
  initialState,
  reducers: {
    updateAddress: (state, action) => {
      const { address } = action.payload
      state.address = address
      state.hub = address?.hub || {}
      state.isAddressPresent =
        address?.isAddressPresent !== undefined
          ? address?.isAddressPresent
          : true
      state.showAddNewAddressPopup = false
      state.isAddressSelectorPopup = false
      state.isChangeAddressPopup = false
      state.addAddress = false
    },
    showAddressPopup: (state) => {
      state.showAddNewAddressPopup = true
      state.isChangeAddressPopup = false
    },
    hideAddressPopup: (state) => {
      state.showAddNewAddressPopup = false
    },
    showAddressSelectorPopup: (state, action) => {
      state.isAddressSelectorPopup = true
      state.isAddressSelectorPopupInCenter = action.payload || false
    },
    hideAddressSelectorPopup: (state) => {
      state.isAddressSelectorPopup = false
    },
    showChangeAddressPopup: (state) => {
      state.isChangeAddressPopup = !state.isChangeAddressPopup
    },
    handleAddNew: (state) => {
      state.addAddress = true
    },
    handleOnCloseClick: (state) => {
      state.showAddNewAddressPopup = false
    },
    handleAddNewAddressPopup: (state) => {
      state.showAddNewAddressPopup = true
      state.isAddressSelectorPopup = false
      state.isChangeAddressPopup = false
    },
    setUseMyLocation: (state, action) => {
      state.useMyLocationEnabled = action.payload
    },
    setUseMyLocationClicked: (state, action) => {
      state.useMyLocationClicked = action.payload
    },
    setBuildingDeliveryEnabled: (state, action) => {
      state.buildingDeliveryEnabled = action.payload
    },
    resetAddress: (state) => {
      clearCookie(CHECKOUT_KEY)
      clearCookie(STORE_ID)
      return initialState
    },
  },
})

export const {
  updateAddress,
  showAddressPopup,
  hideAddressPopup,
  showAddressSelectorPopup,
  hideAddressSelectorPopup,
  showChangeAddressPopup,
  handleAddNew,
  handleOnCloseClick,
  setUseMyLocation,
  setUseMyLocationClicked,
  resetAddress,
  handleAddNewAddressPopup,
  setBuildingDeliveryEnabled,
} = addressSlice.actions

export default addressSlice.reducer
